export type NodeType =
  | 'normal'
  | 'required'
  | 'protected'
  | 'relay'
  | 'shield'
  | 'splitter'
  | 'capacitor'
  | 'core';

export type WireType =
  | 'normal'
  | 'delay'
  | 'directional'
  | 'locked'
  | 'burn'
  | 'forbidden';

export interface NodeDef {
  id: string;
  x: number;
  y: number;
  type: NodeType;
  threshold?: number;
  decay?: number;
  unlocks?: string[];
  shields?: string[];
  splitFactor?: number;
  label?: string;
  /** Plain pass-through node: carries the pulse but is not a tappable starter. */
  conduit?: boolean;
}

export interface WireDef {
  id: string;
  a: string;
  b: string;
  type?: WireType;
  delay?: number;
}

export interface ObjectiveDef {
  requiredNodeIds: string[];
  coreNodeId?: string;
}

export interface LevelDef {
  id: string;
  name: string;
  tier:
    | 'tutorial'
    | 'beginner'
    | 'easy'
    | 'medium'
    | 'hard'
    | 'expert'
    | 'brutal'
    | 'nightmare';
  intro?: string;
  hint?: string;
  /** Early lesson where several taps win on purpose (exempt from the one-winner rule). */
  teaching?: boolean;
  nodes: NodeDef[];
  wires: WireDef[];
  objective: ObjectiveDef;
}

export type SimEvent =
  | { kind: 'tap'; tick: number; nodeId: string }
  | { kind: 'node_collapse'; tick: number; nodeId: string }
  | { kind: 'pulse_emit'; tick: number; wireId: string; fromId: string; toId: string; arriveTick: number }
  | { kind: 'pulse_arrive'; tick: number; wireId: string; toId: string; chargeAfter: number; thresholdAt: number }
  | { kind: 'charge_decay'; tick: number; nodeId: string }
  | { kind: 'wire_open'; tick: number; wireId: string; byRelayId: string }
  | { kind: 'shield_drop'; tick: number; shieldId: string; protectedIds: string[] }
  | { kind: 'wire_burn'; tick: number; wireId: string }
  | { kind: 'core_pulse'; tick: number; coreId: string; pulsesInWindow: number }
  | { kind: 'protected_overload'; tick: number; nodeId: string; byWireId: string }
  | { kind: 'forbidden_trigger'; tick: number; wireId: string };

export type FailureReason =
  | { kind: 'protected_overload'; nodeId: string }
  | { kind: 'forbidden_trigger'; wireId: string }
  | { kind: 'required_not_reached'; nodeId: string }
  | { kind: 'required_under_threshold'; nodeId: string; received: number; needed: number }
  | { kind: 'core_not_collapsed'; coreId: string }
  | { kind: 'core_under_threshold'; coreId: string; received: number; needed: number; windowTicks: number }
  | { kind: 'core_shielded'; coreId: string; shieldIds: string[] }
  | { kind: 'chain_died' };

export interface SimResult {
  outcome: 'win' | 'fail';
  starterId: string;
  events: SimEvent[];
  totalTicks: number;
  failure?: FailureReason;
  finalCharge: Record<string, number>;
  collapsed: Set<string>;
}

/**
 * A node is a valid BREACH POINT (a tappable starter) only if it is a plain
 * access node. Mechanic nodes — required, core, protected, relay, shield,
 * splitter, capacitor — are part of the machine, not entry points. Without
 * this, a player could simply tap the objective node itself (which collapses
 * the starter at tick 0) and trivially win, bypassing the whole puzzle.
 *
 * This is the single source of truth shared by the UI (which nodes are
 * tappable) and the offline auditor (which starters it brute-forces).
 */
export function isBreachable(node: NodeDef): boolean {
  return node.type === 'normal' && !node.conduit;
}
